import React, { useState, useEffect, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { categoryPath } from "@/lib/seo";

export default function CategoryTree({ items, selected, onSelect }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const { byParent, byId } = useMemo(() => {
    const byParent = {}, byId = {};
    items.forEach((c) => {
      byId[c.kategori_id] = c;
      const key = c.ust_kategori_id || "";
      (byParent[key] = byParent[key] || []).push(c);
    });
    return { byParent, byId };
  }, [items]);

  // seçili kategorinin üst dallarını açık tut
  useEffect(() => {
    if (!selected) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let cur = byId[selected];
      while (cur) {
        next.add(cur.kategori_id);
        cur = byId[cur.ust_kategori_id];
      }
      return next;
    });
  }, [selected, byId]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const renderNodes = (parentId, depth) =>
    (byParent[parentId || ""] || []).map((c) => {
      const kids = byParent[c.kategori_id] || [];
      const isOpen = expanded.has(c.kategori_id);
      const isSel = selected === c.kategori_id;
      return (
        <div key={c.kategori_id}>
          <div
            className={`flex items-center rounded-md ${isSel ? "bg-teal-600" : "hover:bg-slate-100"}`}
            style={{ paddingLeft: `${depth * 14}px` }}
          >
            {kids.length > 0 ? (
              <button
                onClick={() => toggle(c.kategori_id)}
                aria-label={`${c.kategori_adi} alt kategorilerini aç/kapat`}
                aria-expanded={isOpen}
                data-testid={`tree-toggle-${c.kategori_id}`}
                className={`p-1.5 shrink-0 ${isSel ? "text-white" : "text-slate-400 hover:text-slate-600"}`}
              >
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>
            ) : (
              <span className="w-[26px] shrink-0" />
            )}
            <a
              href={categoryPath(c)}
              onClick={(e) => { e.preventDefault(); onSelect(isSel ? "" : c.kategori_id); }}
              data-testid={`tree-cat-${c.kategori_id}`}
              className={`flex-1 min-w-0 text-left text-[13px] py-1.5 pr-1 leading-snug ${
                isSel ? "font-semibold text-white" : "text-slate-700"
              }`}
            >
              {c.kategori_adi}
            </a>
            <span className={`text-[11px] pr-2 shrink-0 ${isSel ? "text-teal-100" : "text-slate-400"}`}>
              {c.toplam_urun_sayisi}
            </span>
          </div>
          {isOpen && kids.length > 0 && renderNodes(c.kategori_id, depth + 1)}
        </div>
      );
    });

  return (
    <div className="space-y-0.5" data-testid="category-tree">
      {renderNodes(null, 0)}
    </div>
  );
}
